import { useId, type ReactNode } from 'react'
import { cn } from './cn'
import { FieldContext } from './field-context'
import { t } from '@/lib/i18n'

export interface FieldProps {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  disabled?: boolean
  /** Shows a muted "Optional" note next to the label. */
  optional?: boolean
  /** Element at the right end of the label row, for example a "Forgot password?" link. */
  labelAction?: ReactNode
  /** Override the generated control id. */
  id?: string
  className?: string
  children: ReactNode
}

/** Label above, hint and error below; wires id, aria-describedby and aria-invalid into the control. */
export function Field({ label, hint, error, required, disabled, optional, labelAction, id, className, children }: FieldProps) {
  const autoId = useId()
  const controlId = id ?? `field-${autoId}`
  const hintId = hint ? `${controlId}-hint` : undefined
  const errorId = error ? `${controlId}-error` : undefined
  return (
    <FieldContext.Provider value={{ id: controlId, hintId, errorId, invalid: Boolean(error), disabled, required }}>
      <div className={cn('flex flex-col', className)} data-invalid={error ? true : undefined}>
        {(label || labelAction) && (
          <div className="mb-1.5 flex items-center justify-between gap-3">
            {label && (
              <label htmlFor={controlId} className="text-meta font-semibold text-text">
                {label}
                {required && (
                  <span className="ml-0.5 text-danger" aria-hidden="true">
                    *
                  </span>
                )}
                {optional && <span className="ml-1.5 font-normal text-muted">{t('Optional')}</span>}
              </label>
            )}
            {labelAction && <div className="text-meta">{labelAction}</div>}
          </div>
        )}
        {children}
        {error ? (
          <p id={errorId} className="mt-1.5 text-[12px] leading-4 text-danger" role="alert">
            {error}
          </p>
        ) : null}
        {hint ? (
          <p id={hintId} className="mt-1.5 text-[12px] leading-4 text-muted">
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  )
}
